import type { Dialog as BaseDialog } from '@base-ui/react'
import { type FC, useState } from 'react'
import { TextArea } from '@/components/commons/textarea'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { useAskSecondReview } from '@/features/luxe-submissions/queries/use-ask-second-review'
import { useTranslation } from '@/translations/i18n'

interface MMAskSecondReviewDialogProps {
	handle: BaseDialog.Handle<{ submissionIds: string[] }>
	onSuccess?: () => void
}

export const MMAskSecondReviewDialog: FC<MMAskSecondReviewDialogProps> = ({
	handle,
	onSuccess
}) => {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)

	return (
		<Dialog handle={handle} onOpenChange={setOpen} open={open}>
			{({ payload }) => {
				const data = payload as { submissionIds: string[] } | undefined

				return (
					data !== undefined && (
						<DialogContent
							className='grid max-w-120 grid-rows-[auto_1fr_auto] overflow-hidden'
							showCloseButton={false}
						>
							<DialogHeader>
								{t('luxe_submissions:ask_second_review')}
							</DialogHeader>

							<AskSecondReviewForm
								onSuccess={() => {
									onSuccess?.()
									setOpen(false)
								}}
								submissionIds={data.submissionIds}
							/>
						</DialogContent>
					)
				)
			}}
		</Dialog>
	)
}

function AskSecondReviewForm({
	submissionIds,
	onSuccess
}: {
	submissionIds: string[]
	onSuccess: () => void
}) {
	const { t } = useTranslation()
	const [note, setNote] = useState('')
	const trimmedNote = note.trim()
	const { mutate, isPending } = useAskSecondReview({
		onSuccess
	})

	return (
		<>
			<DialogBody>
				<DialogTitle>
					{t('luxe_submissions:ask_second_review_question')}
				</DialogTitle>

				<TextArea
					label={t('luxe_submissions:ask_second_review_note_label')}
					onChange={(e) => setNote(e.target.value)}
					placeholder={t(
						'luxe_submissions:ask_second_review_note_placeholder'
					)}
					rows={4}
					value={note}
				/>
			</DialogBody>

			<DialogFooter className='flex flex-row justify-between bg-neutral-white'>
				<DialogClose
					render={<ButtonLegacy type='button' variant='tertiary' />}
				>
					{t('common:close')}
				</DialogClose>
				<ButtonLegacy
					disabled={isPending || !trimmedNote}
					onClick={() =>
						mutate({
							submissionIds,
							note: trimmedNote
						})
					}
					type='button'
					variant='brand'
				>
					{t('common:confirm')}
					{isPending && <Spinner />}
				</ButtonLegacy>
			</DialogFooter>
		</>
	)
}
